/** Lux on the dashboard (pure; backend domain/lux.py). Lux amounts arrive as integer hundredths (4250 = 42.50
 * Lux) and money as integer minor units (cents): this module only formats and parses them, with string and integer
 * arithmetic, never floats. The server prices everything; nothing here is a price list.
 *
 * Languages (docs/contributing/i18n.md): the formatters take the reader's locale (`intl.locale`; English digits and
 * separators without one), and the helpers that say something in words take `LuxWords` (`t` and the locale) and
 * answer from the `lux` catalog; without it they keep their English (the admin console, the tests). */
import type { MessageKey } from "../i18n/keys.ts";

/** What the wording helpers need: the dashboard's `t()` (useT) and the reader's locale (useIntl().locale). */
export type LuxWords = { t: (key: MessageKey, values?: Record<string, string | number>) => string; locale: string };

const decimals = new Map<string, string>();
function decimalSign(locale: string): string {
  let sign = decimals.get(locale);
  if (sign === undefined) {
    sign = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal")?.value ?? ".";
    decimals.set(locale, sign);
  }
  return sign;
}

/** "42.50" ("42,50" in German): a Lux amount, always two decimals, thousands separated in the locale's way. */
export function fmtLux(hundredths: number, locale = "en-US"): string {
  const sign = hundredths < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(hundredths));
  const whole = Math.floor(abs / 100);
  const part = abs - whole * 100;
  return `${sign}${whole.toLocaleString(locale)}${decimalSign(locale)}${String(part).padStart(2, "0")}`;
}

/** A price: "1", "5" for whole amounts, "0.10" otherwise. */
export function fmtLuxPrice(hundredths: number, locale = "en-US"): string {
  return hundredths % 100 === 0 ? Math.trunc(hundredths / 100).toLocaleString(locale) : fmtLux(hundredths, locale);
}

/** "$1,234.50" from cents (integer division only). With a locale, the locale's currency format ("1.234,50 $"),
 * the cents still put in from the integer. */
export function fmtMoney(minor: number, currency = "USD", locale?: string): string {
  const sign = minor < 0 ? "-" : "";
  const { whole, part, digits } = splitMinor(minor, currency);
  if (locale) return localMoney(minor < 0, whole, part, currency, locale, digits);
  return `${sign}${symbolOf(currency)}${whole.toLocaleString("en-US")}${digits ? `.${String(part).padStart(digits, "0")}` : ""}`;
}

/** "$1,234" when whole, else "$1,234.50". */
export function fmtMoneyShort(minor: number, currency = "USD", locale?: string): string {
  const { whole, part } = splitMinor(minor, currency);
  if (part) return fmtMoney(minor, currency, locale);
  if (locale) return localMoney(minor < 0, whole, part, currency, locale, 0);
  return `${minor < 0 ? "-" : ""}${symbolOf(currency)}${whole.toLocaleString("en-US")}`;
}

/** Minor-unit digits of a currency (ISO 4217, as the backend's domain/money.py): 2 unless listed. */
const MINOR_DIGITS: Record<string, number> = { JPY: 0, KRW: 0, KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3 };
export function minorDigits(currency: string): number {
  return MINOR_DIGITS[currency.toUpperCase()] ?? 2;
}

function symbolOf(currency: string): string {
  return currency === "USD" ? "$" : `${currency} `;
}

/** A minor-unit amount as whole and fraction integers in its currency's own digits (never a float). */
function splitMinor(minor: number, currency: string): { whole: number; part: number; digits: number } {
  const digits = minorDigits(currency);
  const unit = 10 ** digits;
  const abs = Math.abs(Math.trunc(minor));
  const whole = Math.floor(abs / unit);
  return { whole, part: abs - whole * unit, digits };
}

function localMoney(negative: boolean, whole: number, part: number, currency: string, locale: string, digits: number): string {
  let f: Intl.NumberFormat;
  try {
    f = new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits });
  } catch {
    return `${negative ? "-" : ""}${whole.toLocaleString(locale)}${digits ? decimalSign(locale) + String(part).padStart(digits, "0") : ""} ${currency}`;
  }
  // -0 keeps the minus sign of "-$0.40"; the fraction is the integer's minor units, never a float's
  const text = f
    .formatToParts(negative ? -whole || -0 : whole)
    .map((p) => (p.type === "fraction" ? String(part).padStart(digits, "0") : p.value))
    .join("");
  // in a right-to-left language the amount is one left-to-right run (else "US$ 374" shows as "$US 374")
  return RTL.test(locale) ? `\u2066${text}\u2069` : text;
}

const RTL = /^(ar|he|fa|ur)(-|$)/i;

/** Parses "12", "12.5", "12.50" (Lux) into hundredths; undefined when it is not a Lux amount. */
export function parseLux(text: string): number | undefined {
  const t = text.trim().replace(/,/g, "");
  const m = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return undefined;
  return Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
}

/** "≈ 42 designs or 8 plan sets" (rounded down by the server). */
export function buysText(buys: { designs: number; plan_sets: number }, w?: LuxWords): string {
  if (w) return buys.plan_sets ? w.t("lux.buys.designsOrPlanSets", { designs: buys.designs, planSets: buys.plan_sets }) : w.t("lux.buys.designs", { designs: buys.designs });
  const d = `${buys.designs.toLocaleString("en-US")} design${buys.designs === 1 ? "" : "s"}`;
  if (!buys.plan_sets) return `≈ ${d}`;
  return `≈ ${d} or ${buys.plan_sets.toLocaleString("en-US")} plan set${buys.plan_sets === 1 ? "" : "s"}`;
}

/** Whole percent of `part` in `total` (rounded down), 0..100. */
export function share(part: number, total: number): number {
  if (!(total > 0) || part <= 0) return 0;
  return Math.min(100, Math.floor((part * 100) / total));
}

/** A rate-card line: "1 Lux per design", "0.10 Lux per 25 searches". */
export function rateText(rate: { price: number; block: number; unit: string }, w?: LuxWords): string {
  if (w) return w.t("lux.rates.price", { price: fmtLuxPrice(rate.price, w.locale), per: rateUnit(rate, w) });
  return `${fmtLuxPrice(rate.price)} Lux ${rateUnit(rate)}`;
}

/** The units the API prices in (domain/lux.py, assist/catalogue.py), each with its own "per …" message. */
const UNIT_KEYS: Record<string, MessageKey> = {
  design: "lux.per.design",
  "plan set": "lux.per.planSet",
  search: "lux.per.search",
  lookup: "lux.per.lookup",
  email: "lux.per.email",
  user: "lux.per.user",
  use: "lux.per.use",
  session: "lux.per.session",
};

/** The rate's unit: "per design", "per 25 searches". */
export function rateUnit(rate: { block: number; unit: string }, w?: LuxWords): string {
  const key = UNIT_KEYS[rate.unit];
  if (w && key) return w.t(key, { block: rate.block });
  return `per ${rate.block === 1 ? rate.unit : `${rate.block.toLocaleString(w?.locale ?? "en-US")} ${plural(rate.unit)}`}`;
}

function plural(noun: string): string {
  if (noun.endsWith("ch") || noun.endsWith("sh") || noun.endsWith("s")) return `${noun}es`;
  return `${noun}s`;
}

/** What a Lux price is worth at the plan's Lux price, in cents (rounded half up: for display only). */
export function worthMinor(hundredths: number, unitMinor: number | null | undefined): number | null {
  if (unitMinor == null) return null;
  return Math.floor((hundredths * unitMinor + 50) / 100);
}

/** A pack's total at a plan's Lux price, as the server computes it (integers; the discount rounded half up). */
export function packTotalMinor(unitMinor: number, lux: number, discountPct: number): number {
  const subtotal = unitMinor * lux;
  return subtotal - Math.floor((subtotal * discountPct * 100 + 5000) / 10000);
}

/** The per-Lux price of a pack in cents (rounded down: for display). */
export function perLuxMinor(pack: { lux: number; total_minor: number }): number {
  return pack.lux > 0 ? Math.floor(pack.total_minor / pack.lux) : 0;
}

export const POOL_LABEL: Record<string, string> = {
  reserved: "Design Lux",
  free: "Monthly Lux",
  purchased: "Purchased",
  debt: "Advance",
};

const POOL_KEYS: Record<string, MessageKey> = {
  reserved: "lux.pool.reserved",
  free: "lux.pool.free",
  purchased: "lux.pool.purchased",
  debt: "lux.pool.debt",
};

/** "Design Lux 1.00 · Purchased 0.40": which pools paid (or got back) an operation. */
export function legsText(legs: readonly { pool: string; amount: number }[], w?: LuxWords): string {
  return legs
    .filter((l) => l.amount !== 0)
    .map((l) => {
      const key = POOL_KEYS[l.pool];
      if (w) return key ? w.t(key, { amount: fmtLux(Math.abs(l.amount), w.locale) }) : `${l.pool} ${fmtLux(Math.abs(l.amount), w.locale)}`;
      return `${POOL_LABEL[l.pool] ?? l.pool} ${fmtLux(Math.abs(l.amount))}`;
    })
    .join(" · ");
}

/** A metered action's line: "1,620 of 1,500 included · 0.40 Lux charged". */
export function meterText(m: { used: number; included?: number | null; charged: number; period: string }, w?: LuxWords): string {
  if (w) {
    const values = { used: m.used, period: m.period === "day" ? "day" : "month", charged: m.charged ? fmtLux(m.charged, w.locale) : "none" };
    return m.included == null ? w.t("lux.meter.unlimited", values) : w.t("lux.meter.included", { ...values, included: m.included });
  }
  const used = m.used.toLocaleString("en-US");
  const inc = m.included == null ? "unlimited" : `${m.included.toLocaleString("en-US")} included`;
  const charged = m.charged ? ` · ${fmtLux(m.charged)} Lux charged` : "";
  return `${used} of ${inc} ${m.period === "day" ? "today" : "this month"}${charged}`;
}

/** "Uses 3.42 Lux · worth $29.00": an extra user paid with Lux. */
export function seatQuoteText(q: { lux: number; price_minor: number; currency: string; affordable: boolean }, w?: LuxWords): string {
  if (w) {
    const price = fmtMoney(q.price_minor, q.currency, w.locale);
    return q.affordable ? w.t("lux.seatQuote.uses", { lux: fmtLux(q.lux, w.locale), price }) : w.t("lux.seatQuote.costs", { price });
  }
  if (!q.affordable) return `Costs ${fmtMoney(q.price_minor, q.currency)} of purchased Lux`;
  return `Uses ${fmtLux(q.lux)} Lux · worth ${fmtMoney(q.price_minor, q.currency)}`;
}

/** The activity row's sign and tone. */
export function amountText(hundredths: number, locale = "en-US"): string {
  return hundredths > 0 ? `+${fmtLux(hundredths, locale)}` : hundredths < 0 ? `−${fmtLux(-hundredths, locale)}` : fmtLux(0, locale);
}
